import { useEffect, useRef } from "react";
import usePlayableUrl from "../../hooks/usePlayableUrl";
import { DEFAULT_MUSIC_TIMING, planDriftCorrection } from "./frameClock";

const clampVolume = (volume) => Math.min(1, Math.max(0, volume));
const DRIFT_CHECK_MS = 250;

/**
 * Plays the host's playlist music in the viewer's browser during a direct OBS
 * stream, whose video carries none. It follows the audience timeline the live
 * music player already keeps (song, heard position, playing), which runs
 * behind the host by the HLS delay, plus the host's music volume and the
 * stream's mute button.
 *
 * `startSec` only seeds each song. After that the audio element keeps its
 * own time: the timeline is a one-second counter that lags in a busy or
 * background tab, and seeking to it made the music jump and cut out. The
 * OBS dock has no seek bar, so there is no host seek to follow.
 *
 * The caller must have `startSec` current in the very render that changes
 * `song`. A React state value set next to the Redux dispatch that changes
 * the song renders one tick after the dispatch, so reading it here seeded
 * every new song at the previous song's position.
 *
 * With a frame clock (stamped playlist) the caller keeps `syncTargetRef`
 * at where the host's song is for the frame on screen. A small drift is
 * closed by a slight tempo change (pitch kept), a stalled picture holds the
 * music, and only a drift too large to play out is seeked.
 */
export function useHostMusicAudio({ enabled, song, startSec, playing, hostVolume, muted, syncTargetRef = null, timing = DEFAULT_MUSIC_TIMING }) {
  const timingRef = useRef(timing);
  timingRef.current = timing;
  const audioRef = useRef(null);
  const startRef = useRef(startSec);
  const playingRef = useRef(playing);
  startRef.current = startSec;
  playingRef.current = playing;

  // Signed audio links expire; a failed one is swapped for a fresh link and
  // the song carries on from where it stopped.
  const { url: playableUrl, refresh } = usePlayableUrl(enabled ? song : null);
  const url = enabled ? playableUrl : null;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  // { songId, time } of a song whose link failed mid-play.
  const resumeAtRef = useRef(null);
  const songIdRef = useRef(song?._id);
  songIdRef.current = song?._id;

  useEffect(() => {
    if (!enabled) return undefined;
    const audio = new Audio();
    audio.preload = "auto";
    audio.preservesPitch = true;
    audioRef.current = audio;

    // Browsers hold back sound until the viewer interacts with the page.
    const resumeAfterGesture = () => {
      if (audio.src && audio.paused && playingRef.current) audio.play().catch(() => {});
    };
    document.addEventListener("pointerdown", resumeAfterGesture);
    document.addEventListener("keydown", resumeAfterGesture);

    const refreshAfterError = () => {
      if (!audio.error || !audio.src) return;
      resumeAtRef.current = { songId: songIdRef.current, time: audio.currentTime || 0 };
      refreshRef.current();
    };
    audio.addEventListener("error", refreshAfterError);

    return () => {
      audio.removeEventListener("error", refreshAfterError);
      document.removeEventListener("pointerdown", resumeAfterGesture);
      document.removeEventListener("keydown", resumeAfterGesture);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audioRef.current = null;
    };
  }, [enabled]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!url) {
      audio.pause();
      return;
    }
    audio.src = url;
    // Applied once the metadata loads. A refreshed link resumes the song.
    const resume = resumeAtRef.current;
    audio.currentTime = resume && resume.songId === songIdRef.current ? resume.time : (startRef.current || 0);
    resumeAtRef.current = null;
  }, [url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !url) return;
    if (!playing) {
      audio.pause();
      return;
    }
    audio.play().catch((error) => {
      console.warn("[LiveMusic] host music waiting for a tap to play", { name: error?.name });
    });
  }, [url, playing]);

  useEffect(() => {
    if (!enabled || !syncTargetRef) return undefined;
    let heldForStall = false;
    // A newly loaded song starts where the host's song is by now, not where
    // it was when loading began.
    let syncedSrc = null;
    const timer = setInterval(() => {
      const audio = audioRef.current;
      const target = syncTargetRef.current;
      if (!audio || !audio.src) return;
      if (!target) {
        if (audio.playbackRate !== 1) audio.playbackRate = 1;
        return;
      }
      if (target.stalled) {
        if (!audio.paused) {
          audio.pause();
          heldForStall = true;
        }
        return;
      }
      if (heldForStall) {
        heldForStall = false;
        if (playingRef.current) audio.play().catch(() => {});
      }
      if (audio.readyState < 2) return;
      const expectedSec = target.posSec + (performance.now() - target.atPerfMs) / 1000;
      if (syncedSrc !== audio.src) {
        syncedSrc = audio.src;
        if (Math.abs(audio.currentTime - expectedSec) > 0.25) audio.currentTime = expectedSec;
        return;
      }
      if (audio.paused) {
        // Something outside paused it (a media key, the OS): play on.
        if (playingRef.current) audio.play().catch(() => {});
        return;
      }
      const plan = planDriftCorrection({ driftSec: audio.currentTime - expectedSec, rate: audio.playbackRate, timing: timingRef.current });
      if (plan.action === "seek") {
        console.log("[LiveMusic] host music seeked to the picture", { fromSec: audio.currentTime, toSec: expectedSec });
        audio.currentTime = expectedSec;
        audio.playbackRate = 1;
      } else if (plan.action === "rate" && audio.playbackRate !== plan.rate) {
        audio.playbackRate = plan.rate;
      }
    }, DRIFT_CHECK_MS);
    return () => clearInterval(timer);
  }, [enabled, syncTargetRef]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = clampVolume((hostVolume ?? 0) * timing.musicVolumeScale);
    audio.muted = Boolean(muted);
  }, [enabled, hostVolume, muted, timing.musicVolumeScale]);
}
